import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useToast } from '../../context/ToastContext';

const AdminLayout = ({ children, title }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { success } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await signOut(auth);
    success('Logged out successfully');
    navigate('/');
  };

  const navItems = [
    { path: '/admin',          label: 'Dashboard',    icon: '📊', exact: true },
    { path: '/admin/posts',    label: 'News & Posts', icon: '📰' },
    { path: '/admin/profiles', label: 'Profiles',     icon: '👥' },
    { path: '/admin/users',    label: 'Users',        icon: '👤' },
  ];

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="admin-sidebar__brand">
          <span className="brand-icon">🏺</span>
          {sidebarOpen && (
            <div>
              <div className="brand-name">Kumbhar Samaj</div>
              <div className="brand-role">Admin Panel</div>
            </div>
          )}
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <nav className="admin-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              className={({ isActive }) => `admin-nav__item ${isActive ? 'active' : ''}`}
            >
              <span className="nav-icon">{item.icon}</span>
              {sidebarOpen && <span className="nav-label">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          {sidebarOpen && (
            <div className="admin-user">
              <div className="admin-avatar">
                {currentUser?.displayName?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="admin-user-info">
                <div className="admin-user-name">{currentUser?.displayName || 'Admin'}</div>
                <div className="admin-user-email">{currentUser?.email}</div>
              </div>
            </div>
          )}
          <button className="admin-logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <h1 className="admin-page-title">{title}</h1>
          <NavLink to="/" className="btn btn-outline btn-sm" target="_blank">
            🌐 View Site
          </NavLink>
        </header>
        <div className="admin-content">{children}</div>
      </div>

      <style jsx>{`
        .admin-layout {
          display: flex;
          min-height: 100vh;
          background: var(--neutral-100);
          font-family: var(--font-body);
        }
        .admin-sidebar {
          width: 260px;
          background: var(--primary-900);
          display: flex;
          flex-direction: column;
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          z-index: 200;
          transition: width var(--transition-base);
          overflow: hidden;
        }
        .admin-sidebar.closed { width: 68px; }

        .admin-sidebar__brand {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-4);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          min-height: 68px;
        }
        .brand-icon { font-size: 1.8rem; flex-shrink: 0; }
        .brand-name {
          font-family: var(--font-display);
          font-size: var(--text-base);
          font-weight: var(--weight-bold);
          color: white;
          white-space: nowrap;
        }
        .brand-role {
          font-size: var(--text-xs);
          color: #e5c589;
          font-weight: var(--weight-medium);
          white-space: nowrap;
        }
        .sidebar-toggle {
          margin-left: auto;
          background: rgba(255,255,255,0.1);
          border: none;
          color: rgba(255,255,255,0.7);
          width: 28px;
          height: 28px;
          border-radius: var(--radius-md);
          cursor: pointer;
          font-size: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all var(--transition-fast);
        }
        .sidebar-toggle:hover { background: rgba(255,255,255,0.2); color: white; }

        .admin-nav {
          flex: 1;
          padding: var(--space-4) var(--space-2);
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
          overflow-y: auto;
        }
        .admin-nav__item {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3);
          border-radius: var(--radius-lg);
          text-decoration: none;
          color: rgba(255,255,255,0.65);
          font-size: var(--text-sm);
          font-weight: var(--weight-medium);
          transition: all var(--transition-fast);
          white-space: nowrap;
        }
        .admin-nav__item:hover { background: rgba(255,255,255,0.08); color: white; }
        .admin-nav__item.active {
          background: var(--primary-500);
          color: white;
          box-shadow: 0 4px 12px rgba(201,104,67,0.3);
        }
        .nav-icon { font-size: 1.1rem; flex-shrink: 0; width: 20px; text-align: center; }

        .admin-sidebar__footer {
          padding: var(--space-4);
          border-top: 1px solid rgba(255,255,255,0.1);
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
        }
        .admin-user {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          overflow: hidden;
        }
        .admin-avatar {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary-500), var(--gold-500));
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--weight-bold);
          font-size: var(--text-sm);
          flex-shrink: 0;
        }
        .admin-user-name {
          font-size: var(--text-sm);
          font-weight: var(--weight-semibold);
          color: white;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-user-email {
          font-size: 10px;
          color: rgba(255,255,255,0.5);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .admin-logout-btn {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          width: 100%;
          padding: var(--space-2) var(--space-3);
          background: rgba(255,255,255,0.06);
          border: none;
          border-radius: var(--radius-md);
          color: rgba(255,255,255,0.6);
          font-size: var(--text-sm);
          cursor: pointer;
          transition: all var(--transition-fast);
          font-family: var(--font-body);
          white-space: nowrap;
        }
        .admin-logout-btn:hover { background: rgba(239,68,68,0.2); color: #fca5a5; }

        .admin-main {
          flex: 1;
          margin-left: 260px;
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          transition: margin-left var(--transition-base);
        }
        .admin-sidebar.closed ~ .admin-main { margin-left: 68px; }

        .admin-topbar {
          background: white;
          padding: var(--space-4) var(--space-8);
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid var(--neutral-200);
          box-shadow: var(--shadow-xs);
          position: sticky;
          top: 0;
          z-index: 50;
        }
        .admin-page-title {
          font-size: var(--text-2xl);
          color: var(--primary-900);
          margin: 0;
        }
        .admin-content {
          padding: var(--space-8);
          flex: 1;
        }

        @media (max-width: 768px) {
          .admin-sidebar { width: 68px; }
          .admin-main { margin-left: 68px; }
          .brand-name, .brand-role, .nav-label, .admin-user-info { display: none; }
          .admin-content { padding: var(--space-4); }
          .admin-topbar { padding: var(--space-4); }
        }
      `}</style>
    </div>
  );
};

export default AdminLayout;